/**
 * Authentication Context
 *
 * Provides authentication state and methods for the app.
 * Uses localStorage for session persistence and environment variable for password.
 */

import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

const AuthContext = createContext();

// Session duration (24 hours)
const SESSION_DURATION = 24 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    const checkSession = () => {
      try {
        const session = localStorage.getItem("social-hub-session");
        if (session) {
          const sessionData = JSON.parse(session);
          const now = new Date().getTime();

          // Check if session is still valid
          if (sessionData.timestamp && now - sessionData.timestamp < SESSION_DURATION) {
            setIsAuthenticated(true);
          } else {
            // Session expired, remove it
            localStorage.removeItem("social-hub-session");
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
        localStorage.removeItem("social-hub-session");
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  const login = (password) => {
    const correctPassword = process.env.REACT_APP_AUTH_PASSWORD;

    if (!correctPassword) {
      console.error("Auth password not configured");
      return false;
    }

    if (password === correctPassword) {
      // Create session
      const sessionData = {
        authenticated: true,
        timestamp: new Date().getTime(),
      };

      localStorage.setItem("social-hub-session", JSON.stringify(sessionData));
      setIsAuthenticated(true);
      return true;
    }

    return false;
  };

  const logout = () => {
    localStorage.removeItem("social-hub-session");
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
